import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TableSortLabel,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TablePagination,
  LinearProgress,
  Divider,
} from '@mui/material';
import { InfoOutlined, Search as SearchIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { Account } from '../types';
import { EntityDetailsModal } from '../components/EntityDetailsModal';
import { FeaturesModal } from '../components/FeaturesModal';
import { useConfigStore } from '../stores/useConfigStore';
import { getTableEntityColumns } from '../utils/entityOrder';
import { buildStageColorMap } from '../utils/stageColors';

type SortField = 'companyName' | 'healthScore' | 'onboardingStage' | 'daysSinceLastActivity' | 'clientSince';
type SortOrder = 'asc' | 'desc';

interface EntityColumn {
  type: string;
  displayName: string;
}

/**
 * Accounts Page
 * List and manage customer accounts with sortable table
 */
export const AccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const { config, loadConfig } = useConfigStore();

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('healthScore');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loadingPages, setLoadingPages] = useState(false);
  const [serverTotal, setServerTotal] = useState(0);

  // Entity details modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<{
    type: string;
    data: {
      count: number;
      firstAt?: string;
      lastAt?: string;
      daysSinceFirst?: number;
      daysSinceLast?: number;
      ttf?: number;
      last7Days?: number;
      last30Days?: number;
      totalAmount?: number;
    } | null;
    companyName: string;
  } | null>(null);

  // Features modal state
  const [featuresModalOpen, setFeaturesModalOpen] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<{
    features: Record<string, {
      enabled: boolean;
      firstUsedAt?: string;
      lastUsedAt?: string;
      lastDisabledAt?: string;
      usageCount?: number;
      [key: string]: any;
    }>;
    companyName: string;
  } | null>(null);

  // Billing limit info
  const [hiddenCount, setHiddenCount] = useState(0);
  const [accountLimit, setAccountLimit] = useState(0);
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [, setBillingPlan] = useState('free');

  useEffect(() => {
    loadAccounts();
  }, []);

  /**
   * Loads every page of the org's accounts (200 per request, capped) so that
   * search, stage filter and sorting operate on the full set. The first page
   * renders immediately; the rest stream in with a progress bar.
   */
  const loadAccounts = async () => {
    const PER_PAGE = 200;
    const MAX_PAGES = 25;
    try {
      setLoading(true);
      setError(null);
      // Fetch archived too; they are hidden client-side unless the Stage filter is "Archived"
      const first = await api.listAccounts({ page: 1, perPage: PER_PAGE, sortBy: 'createdAt', sortOrder: 'asc', archived: 'include' });
      setAccounts(first.accounts);
      setServerTotal(first.total);
      setHiddenCount(first.hiddenCount);
      setAccountLimit(first.accountLimit);
      setTotalAccounts(first.totalAccounts);
      setBillingPlan(first.plan);
      setLoading(false);

      if (first.accounts.length < first.total) {
        setLoadingPages(true);
        let all = first.accounts;
        for (let p = 2; p <= MAX_PAGES && all.length < first.total; p++) {
          const next = await api.listAccounts({ page: p, perPage: PER_PAGE, sortBy: 'createdAt', sortOrder: 'asc', archived: 'include' });
          if (next.accounts.length === 0) break;
          all = all.concat(next.accounts);
          setAccounts(all);
        }
        setLoadingPages(false);
      }
    } catch (err) {
      setError('Failed to load accounts. Please try again.');
      console.error('Error loading accounts:', err);
      setLoading(false);
      setLoadingPages(false);
    }
  };

  // Reset to the first page whenever the visible set changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery, stageFilter, sortField, sortOrder]);

  // Entity columns: config-driven with auto-detect fallback
  const entityColumns: EntityColumn[] = useMemo(() => {
    return getTableEntityColumns(config?.entityDefinitions, accounts);
  }, [accounts, config?.entityDefinitions]);

  const handleSort = (field: SortField) => {
    const isAsc = sortField === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  const visibleAccounts = useMemo(() => accounts.filter(a => !a.archived), [accounts]);
  const archivedAccounts = useMemo(() => accounts.filter(a => !!a.archived), [accounts]);

  // Unique stages for filter dropdown (archived is not a stage; it is offered separately)
  const availableStages = useMemo(() => {
    const stages = new Set(visibleAccounts.map(a => a.onboardingStage));
    return Array.from(stages).sort();
  }, [visibleAccounts]);

  const sortedAccounts = useMemo(() => {
    // Archived accounts only appear when explicitly selected
    const pool = stageFilter === 'archived' ? archivedAccounts : visibleAccounts;
    // Apply search and stage filter first
    const filtered = pool.filter(a => {
      const name = (a.metadata?.companyName || a.companyName || a.accountId).toLowerCase();
      const email = (a.metadata?.ownerEmail || a.ownerEmail || '').toLowerCase();
      const matchesSearch = !searchQuery ||
        name.includes(searchQuery.toLowerCase()) ||
        email.includes(searchQuery.toLowerCase());
      const matchesStage = stageFilter === 'all' || stageFilter === 'archived' || a.onboardingStage === stageFilter;
      return matchesSearch && matchesStage;
    });

    const sorted = [...filtered].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (sortField === 'companyName') {
        aValue = (a.metadata?.companyName || a.accountId).toLowerCase();
        bValue = (b.metadata?.companyName || b.accountId).toLowerCase();
      } else if (sortField === 'clientSince') {
        aValue = a.metadata?.clientSince || a.createdAt;
        bValue = b.metadata?.clientSince || b.createdAt;
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      // Handle string comparisons
      if (typeof aValue === 'string' && sortField !== 'clientSince') {
        aValue = aValue.toLowerCase();
      }
      if (typeof bValue === 'string' && sortField !== 'clientSince') {
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [visibleAccounts, archivedAccounts, sortField, sortOrder, searchQuery, stageFilter]);

  const pagedAccounts = useMemo(
    () => sortedAccounts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedAccounts, page, rowsPerPage]
  );

  const getHealthScoreColor = (score: number): string => {
    if (score >= 75) return '#4caf50'; // Green - Healthy
    if (score >= 50) return '#FFB800'; // Yellow - Moderate
    if (score >= 25) return '#ff9800'; // Orange - At Risk
    return '#f44336'; // Red - Critical
  };

  const getHealthScoreLabel = (score: number): string => {
    if (score >= 75) return 'Healthy';
    if (score >= 50) return 'Moderate';
    if (score >= 25) return 'At Risk';
    return 'Critical';
  };

  const stageColorMap = useMemo(
    () => buildStageColorMap(config?.stageConfig),
    [config?.stageConfig]
  );

  const getSubscriptionStatus = (account: Account): string => {
    const status = account.subscription?.status?.toLowerCase();
    if (!status || status === 'none') return 'none';
    if (status === 'trialing' || status === 'trial') return 'trial';
    if (status === 'active') return 'active';
    if (status === 'canceled' || status === 'cancelled' || status === 'ended') return 'ended';
    if (status === 'past_due') return 'past_due';
    return 'none';
  };

  const getSubscriptionColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trial':
        return 'primary';
      case 'past_due':
        return 'warning';
      case 'ended':
        return 'error';
      default:
        return 'default';
    }
  };

  const getSubscriptionLabel = (status: string): string => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'trial':
        return 'Trial';
      case 'past_due':
        return 'Past Due';
      case 'ended':
        return 'Ended';
      default:
        return 'No Subscription';
    }
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 30) return `${diffInDays}d ago`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)}mo ago`;
    return date.toLocaleDateString();
  };

  const getCompanyName = (account: Account): string => {
    return account.metadata?.companyName || account.accountId;
  };

  const getOwnerEmail = (account: Account): string => {
    return account.metadata?.ownerEmail || account.ownerEmail || '-';
  };

  const getOwnerName = (account: Account): string => {
    const firstName = account.metadata?.ownerFirstName || '';
    const lastName = account.metadata?.ownerLastName || '';
    const ownerName = account.metadata?.ownerName || '';

    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    return ownerName || 'Unknown';
  };

  const getEntityCount = (account: Account, entityType: string): number => {
    return account.entities?.[entityType]?.count || 0;
  };

  const handleEntityClick = (account: Account, entityType: string) => {
    const entityData = account.entities?.[entityType];
    if (entityData) {
      setSelectedEntity({
        type: entityType,
        data: entityData,
        companyName: getCompanyName(account),
      });
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedEntity(null);
  };

  const handleFeaturesClick = (account: Account) => {
    const features = account.features;
    if (features && Object.keys(features).length > 0) {
      setSelectedFeatures({
        features,
        companyName: getCompanyName(account),
      });
      setFeaturesModalOpen(true);
    }
  };

  const handleFeaturesModalClose = () => {
    setFeaturesModalOpen(false);
    setSelectedFeatures(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Accounts
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Accounts
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {stageFilter === 'archived'
              ? `${sortedAccounts.length} archived account${sortedAccounts.length === 1 ? '' : 's'} (churned 60+ days, not billed)`
              : sortedAccounts.length === visibleAccounts.length
                ? `${visibleAccounts.length} account${visibleAccounts.length === 1 ? '' : 's'}`
                : `${sortedAccounts.length} of ${visibleAccounts.length} accounts`}
            {loadingPages && serverTotal > accounts.length ? ` (loading ${accounts.length}/${serverTotal}…)` : ''}
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Stage</InputLabel>
            <Select
              value={stageFilter}
              label="Stage"
              onChange={(e) => setStageFilter(e.target.value)}
            >
              <MenuItem value="all">All Stages</MenuItem>
              {availableStages.map(stage => (
                <MenuItem key={stage} value={stage}>
                  {stage.replace(/_/g, ' ')}
                </MenuItem>
              ))}
              {archivedAccounts.length > 0 && <Divider />}
              {archivedAccounts.length > 0 && (
                <MenuItem value="archived">Archived ({archivedAccounts.length})</MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Upgrade banner when accounts are hidden */}
      {hiddenCount > 0 && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, rgba(220,249,149,0.08) 0%, rgba(220,249,149,0.02) 100%)',
            border: '1px solid rgba(220,249,149,0.15)',
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={600}>
              Showing {accountLimit} of {totalAccounts} accounts
            </Typography>
            <Typography variant="caption" color="text.secondary">
              +{hiddenCount} more {hiddenCount === 1 ? 'account' : 'accounts'} available with an upgraded plan
            </Typography>
          </Box>
          <Chip
            label="Upgrade"
            clickable
            onClick={() => navigate('/account/billing')}
            sx={{
              bgcolor: '#DCF995',
              color: '#1B0826',
              fontWeight: 700,
              '&:hover': { bgcolor: '#c8e57e' },
            }}
          />
        </Paper>
      )}

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        {loadingPages && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'companyName'}
                  direction={sortField === 'companyName' ? sortOrder : 'asc'}
                  onClick={() => handleSort('companyName')}
                >
                  Account
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'healthScore'}
                  direction={sortField === 'healthScore' ? sortOrder : 'asc'}
                  onClick={() => handleSort('healthScore')}
                >
                  Health
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'onboardingStage'}
                  direction={sortField === 'onboardingStage' ? sortOrder : 'asc'}
                  onClick={() => handleSort('onboardingStage')}
                >
                  Stage
                </TableSortLabel>
              </TableCell>
              <TableCell>First Entity</TableCell>
              <TableCell>Subscription</TableCell>
              <TableCell align="center">Features</TableCell>
              {entityColumns.map((entity) => (
                <TableCell key={entity.type} align="center">
                  {entity.displayName}
                </TableCell>
              ))}
              <TableCell>Last Login</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'daysSinceLastActivity'}
                  direction={sortField === 'daysSinceLastActivity' ? sortOrder : 'asc'}
                  onClick={() => handleSort('daysSinceLastActivity')}
                >
                  Last Active
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'clientSince'}
                  direction={sortField === 'clientSince' ? sortOrder : 'asc'}
                  onClick={() => handleSort('clientSince')}
                >
                  Client Since
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedAccounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9 + entityColumns.length} align="center" sx={{ py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    No accounts found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pagedAccounts.map((account) => (
                <TableRow
                  key={account.accountId}
                  hover
                  onClick={() => navigate(`/accounts/${account.accountId}`)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {getCompanyName(account)}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            {getOwnerEmail(account)}
                          </Typography>
                          <Tooltip
                            title={
                              <Box>
                                <Typography variant="caption" display="block">
                                  <strong>Owner:</strong> {getOwnerName(account)}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  <strong>Account ID:</strong> {account.accountId}
                                </Typography>
                              </Box>
                            }
                            arrow
                          >
                            <IconButton size="small" sx={{ padding: 0 }}>
                              <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: getHealthScoreColor(account.healthScore),
                        }}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {account.healthScore}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({getHealthScoreLabel(account.healthScore)})
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={account.onboardingStage.replace(/_/g, ' ')}
                      size="small"
                      sx={{
                        textTransform: 'capitalize',
                        bgcolor: stageColorMap[account.onboardingStage] || '#1976d2',
                        color: '#fff',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {account.ttfFirstEntity !== undefined && account.firstEntityType ? (
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {account.firstEntityType}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {account.ttfFirstEntity === 0
                            ? 'same day'
                            : `${account.ttfFirstEntity}d`}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getSubscriptionLabel(getSubscriptionStatus(account))}
                      color={getSubscriptionColor(getSubscriptionStatus(account))}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {(() => {
                      // Extract enabled features
                      const booleanFeatures = account.features
                        ? Object.entries(account.features).filter(([_, feature]) => feature.enabled)
                        : [];
                      const count = booleanFeatures.length;

                      if (count === 0) {
                        return (
                          <Typography variant="body2" fontWeight={500} color="text.secondary">
                            -
                          </Typography>
                        );
                      } else if (count === 1) {
                        return (
                          <Typography variant="body2" fontWeight={500}>
                            {booleanFeatures[0][0]}
                          </Typography>
                        );
                      } else {
                        return (
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{
                              cursor: 'pointer',
                              color: 'secondary.main',
                              textDecoration: 'underline',
                              textDecorationStyle: 'dotted',
                              '&:hover': {
                                textDecoration: 'underline',
                                textDecorationStyle: 'solid',
                                color: 'secondary.dark',
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFeaturesClick(account);
                            }}
                          >
                            {count}
                          </Typography>
                        );
                      }
                    })()}
                  </TableCell>
                  {entityColumns.map((entity) => {
                    const count = getEntityCount(account, entity.type);
                    const hasData = count > 0 && account.entities?.[entity.type];

                    return (
                      <TableCell key={entity.type} align="center">
                        {hasData ? (
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{
                              cursor: 'pointer',
                              color: 'secondary.main',
                              textDecoration: 'underline',
                              textDecorationStyle: 'dotted',
                              '&:hover': {
                                textDecoration: 'underline',
                                textDecorationStyle: 'solid',
                                color: 'secondary.dark',
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEntityClick(account, entity.type);
                            }}
                          >
                            {count}
                          </Typography>
                        ) : (
                          <Typography variant="body2" fontWeight={500} color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <Typography variant="body2" color={account.lastLoginAt ? 'text.primary' : 'text.secondary'}>
                      {account.lastLoginAt ? formatDate(account.lastLoginAt) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {account.daysSinceLastActivity === undefined || account.daysSinceLastActivity === null
                        ? 'Never'
                        : account.daysSinceLastActivity === 0
                        ? 'Today'
                        : `${account.daysSinceLastActivity}d ago`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(account.metadata?.clientSince || account.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={sortedAccounts.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </TableContainer>

      {/* Entity Details Modal */}
      <EntityDetailsModal
        open={modalOpen}
        onClose={handleModalClose}
        entityType={selectedEntity?.type || ''}
        entityData={selectedEntity?.data || null}
        companyName={selectedEntity?.companyName || ''}
      />

      {/* Features Modal */}
      <FeaturesModal
        open={featuresModalOpen}
        onClose={handleFeaturesModalClose}
        features={selectedFeatures?.features}
        companyName={selectedFeatures?.companyName || ''}
      />
    </Box>
  );
};
